import { AppShell, PageTitle } from "@/components/app-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { OrgOverviewSkeleton } from "@/components/mission-skeletons";
import { createProject, fetchSessionUser, getOrg, type ProjectMember } from "@/lib/api";
import { projectMemberPrimaryLabel, projectMemberSecondaryLine } from "@/lib/project-member-label";
import {
  canCreateOrgProject,
  createProjectBody,
  createProjectErrorField,
  createProjectHomePath,
  formatCreateProjectError,
  resolveCreateProjectFields,
  slugifyProject,
} from "@/lib/create-project";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useKeyedLoad } from "@/lib/scope-loading";

type ProjectRow = { id: string; slug: string; name: string };
type MemberRow = Pick<ProjectMember, "user_id" | "role" | "name" | "email" | "created_at">;

export function OrgPage() {
  const { orgSlug } = useParams();
  const nav = useNavigate();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const { loading, start, finish } = useKeyedLoad(orgSlug ?? "");
  const loaded = !loading;

  const canCreate = canCreateOrgProject(userId, members);
  const errField = createProjectErrorField(createErr);

  useEffect(() => {
    const isCurrent = start();
    if (!orgSlug) {
      finish(isCurrent);
      return;
    }
    let cancelled = false;
    setLoadErr(null);
    setCreateErr(null);
    setProjects([]);
    setMembers([]);
    setUserId(null);
    setName("");
    setSlug("");
    setSlugTouched(false);
    setBusy(false);
    void (async () => {
      try {
        const user = await fetchSessionUser();
        if (!cancelled) setUserId(user?.id ?? null);
      } catch {
        if (!cancelled) setUserId(null);
      }
      try {
        const orgBody = await getOrg(orgSlug);
        if (cancelled) return;
        setProjects(orgBody.projects ?? []);
        setMembers(orgBody.members ?? []);
        setLoadErr(null);
      } catch (e) {
        if (cancelled) return;
        setProjects([]);
        setMembers([]);
        setLoadErr(e instanceof Error ? e.message : "org load failed");
      } finally {
        if (!cancelled) finish(isCurrent);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgSlug, start, finish]);

  function onNameChange(value: string) {
    setCreateErr(null);
    setName(value);
    if (!slugTouched) setSlug(value.trim() ? slugifyProject(value) : "");
  }

  function onSlugChange(value: string) {
    setCreateErr(null);
    setSlugTouched(true);
    setSlug(value);
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!orgSlug || busy) return;
    const resolved = resolveCreateProjectFields(name, slug, projects);
    if (resolved.error) {
      setCreateErr(resolved.error);
      return;
    }
    setBusy(true);
    setCreateErr(null);
    try {
      const created = await createProject(orgSlug, createProjectBody(resolved.name, resolved.slug));
      nav(createProjectHomePath(orgSlug, created.slug));
    } catch (ex) {
      setCreateErr(formatCreateProjectError(ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell title={<PageTitle kicker="organization">{orgSlug}</PageTitle>} showAgent={false}>
      {!loaded ? (
        <OrgOverviewSkeleton />
      ) : (
      <div className="space-y-4">
        <Card data-testid="create-project">
          <CardHeader>
            <CardTitle className="text-sm">Create project</CardTitle>
            <CardDescription>
              Hosting is on-platform (D1 + R2 + libgit2). GitHub is not required. After create, upload a KiCad zip
              from Design / Hardware.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadErr ? (
              <p className="text-sm text-falcon" role="alert">
                {loadErr}
              </p>
            ) : canCreate ? (
              <form className="max-w-lg space-y-3" onSubmit={(e) => void onCreate(e)}>
                <div className="space-y-1.5">
                  <Label htmlFor="project-name">name</Label>
                  <Input
                    id="project-name"
                    name="name"
                    required
                    value={name}
                    disabled={busy}
                    aria-invalid={errField === "name" || undefined}
                    aria-describedby={createErr ? "create-project-error" : undefined}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder="Receiver baseboard"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="project-slug">slug</Label>
                  <Input
                    id="project-slug"
                    name="slug"
                    className="font-mono"
                    value={slug}
                    disabled={busy}
                    aria-invalid={errField === "slug" || undefined}
                    aria-describedby={createErr ? "create-project-error" : undefined}
                    onChange={(e) => onSlugChange(e.target.value)}
                    placeholder="receiver-baseboard"
                    autoComplete="off"
                  />
                  {orgSlug && slug.trim() ? (
                    <p className="font-mono text-xs text-muted-foreground">
                      {orgSlug}/{slug.trim().toLowerCase()}
                    </p>
                  ) : null}
                </div>
                {createErr ? (
                  <p id="create-project-error" className="text-sm text-falcon" data-testid="create-project-error" role="alert">
                    {createErr}
                  </p>
                ) : null}
                <Button type="submit" size="sm" disabled={busy || !name.trim()} aria-busy={busy} data-testid="create-project-submit" data-mission-danger="">
                  {busy ? "Creating…" : "Create project"}
                </Button>
              </form>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="create-project-forbidden">
                Only organization members can create a project. Ask an owner to add you, then retry. The API returns
                403 if you are not a member.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Projects</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {projects.map((p) => (
                <Link
                  key={p.id}
                  to={`/o/${orgSlug}/p/${p.slug}/files`}
                  className="flex items-center justify-between rounded-md border border-border p-3 no-underline hover:bg-muted"
                >
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {orgSlug}/{p.slug}
                    </div>
                  </div>
                  <Badge variant="outline">open</Badge>
                </Link>
              ))}
              {projects.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {canCreate
                    ? "No projects yet. Create one above — hosting is on-platform; zip upload is next."
                    : "No projects yet. Organization members can create a hosted project here."}
                </p>
              )}
            </CardContent>
          </Card>
          <Card id="members">
            <CardHeader>
              <CardTitle className="text-sm">Members</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m) => {
                    const secondary = projectMemberSecondaryLine(m);
                    return (
                      <TableRow key={m.user_id}>
                        <TableCell>
                          <div className="text-sm font-medium">{projectMemberPrimaryLabel(m)}</div>
                          {secondary ? (
                            <div className="text-xs text-muted-foreground">{secondary}</div>
                          ) : null}
                        </TableCell>
                        <TableCell>{m.role}</TableCell>
                      </TableRow>
                    );
                  })}
                  {members.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} className="text-sm text-muted-foreground">
                        No organization members to show.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
      )}
    </AppShell>
  );
}
